/**
 * The chunked map's store and builder (docs/archive/OPEN_WORLD.md §4.1, lane A): a `ChunkStore` over a flat
 * table of chunks built on first touch, with an optional LRU cap for the client, and `chunkedMap`,
 * a `GameMap` whose layers resolve through the store (the accessors in `map.ts`).
 */
import { CHUNK, REGION_CHUNKS, type ChunkData, type ChunkGenerator, type ChunkStore } from './chunk';
import type { GameMap } from '../map';

export interface ChunkStoreOptions {
  /** Chunks along the side (default `REGION_CHUNKS`). */
  readonly chunks?: number;
  /**
   * The most chunks kept built (the client's LRU); the least recently touched clean chunk is
   * evicted past it. Chunks written to (`dirty`) are never evicted. Default: no cap (the server).
   */
  readonly cap?: number;
}

/** A lazy `ChunkStore`: a flat `chunks`² table, each chunk generated on first touch. */
export class LazyChunkStore implements ChunkStore {
  readonly chunks: number;
  private readonly table: (ChunkData | undefined)[];
  private readonly used: Float64Array;
  private readonly written: Uint8Array;
  private readonly cap: number;
  private tick = 0;
  /** Chunks built now. */
  size = 0;
  /** Chunks generated so far (rebuilds after eviction included). */
  generated = 0;
  /** Chunks put in by `install` (built elsewhere). */
  installed = 0;

  constructor(
    private readonly gen: ChunkGenerator,
    opts: ChunkStoreOptions = {},
  ) {
    this.chunks = opts.chunks ?? REGION_CHUNKS;
    const n = this.chunks * this.chunks;
    this.table = new Array<ChunkData | undefined>(n);
    this.used = new Float64Array(n);
    this.written = new Uint8Array(n);
    this.cap = opts.cap ?? Infinity;
  }

  ensure(ci: number, cj: number): ChunkData {
    const k = cj * this.chunks + ci;
    let c = this.table[k];
    if (!c) {
      if (ci < 0 || cj < 0 || ci >= this.chunks || cj >= this.chunks)
        throw new Error(`chunk ${ci},${cj} out of range`);
      c = this.gen(ci, cj);
      this.table[k] = c;
      this.size++;
      this.generated++;
      if (this.size > this.cap) this.evictOldest(k);
    }
    if (this.cap !== Infinity) this.used[k] = ++this.tick;
    return c;
  }

  peek(ci: number, cj: number): ChunkData | undefined {
    if (ci < 0 || cj < 0 || ci >= this.chunks || cj >= this.chunks) return undefined;
    return this.table[cj * this.chunks + ci];
  }

  install(chunk: ChunkData): boolean {
    const { ci, cj } = chunk;
    if (ci < 0 || cj < 0 || ci >= this.chunks || cj >= this.chunks) return false;
    const k = cj * this.chunks + ci;
    if (this.table[k]) return false;
    this.table[k] = chunk;
    this.size++;
    this.installed++;
    if (this.cap !== Infinity) this.used[k] = ++this.tick;
    if (this.size > this.cap) this.evictOldest(k);
    return true;
  }

  evict(ci: number, cj: number): void {
    const k = cj * this.chunks + ci;
    if (!this.table[k] || this.written[k]) return;
    this.table[k] = undefined;
    this.size--;
  }

  dirty(ci: number, cj: number): void {
    this.written[cj * this.chunks + ci] = 1;
  }

  private evictOldest(keep: number): void {
    let best = -1;
    for (let k = 0; k < this.table.length; k++)
      if (k !== keep && this.table[k] && !this.written[k] && (best < 0 || this.used[k]! < this.used[best]!))
        best = k;
    if (best >= 0) this.evict(best % this.chunks, (best / this.chunks) | 0);
  }
}

/** The fields of a chunked `GameMap` other than its size and layers. */
export type ChunkedMapProps = Omit<
  GameMap,
  | 'w'
  | 'h'
  | 'tiles'
  | 'variant'
  | 'owner'
  | 'level'
  | 'edge'
  | 'flight'
  | 'surface'
  | 'safe'
  | 'made'
  | 'deep'
  | 'chunks'
>;

const NONE8 = new Uint8Array(0);
const NONE16 = new Uint16Array(0);

/**
 * A `GameMap` backed by chunks: `w` = `h` = `store.chunks × CHUNK`. Its `tiles`, `variant` and
 * `owner` arrays are empty: read and write layers only through the accessors (`tileAt`,
 * `variantAt`, `levelCm`…, `setTileAt`).
 */
export function chunkedMap(store: ChunkStore, props: ChunkedMapProps): GameMap {
  const side = store.chunks * CHUNK;
  return { ...props, w: side, h: side, tiles: NONE8, variant: NONE8, owner: NONE16, chunks: store };
}
